import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import noData from 'highcharts/modules/no-data-to-display';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import SimplifiedAudience from '../../../../shared/components/Analytics/SimplifiedAudience';
import i18n from '../../../../utils/i18n';
import TendenceTable from './TendenceTable';

noData(Highcharts);

const createPieChart = (data, title, limit = 9) => {
  const orderedData = Object.keys(data || {}).map(key => ({
    name: data[key].name,
    y: data[key].percent
  })).sort((a, b) => (a.percent * 100) - (b.percent * 100));

  const dataSeries = [...orderedData.slice(0, limit)];

  let othersCount = 0;
  orderedData.slice(limit + 1).forEach(elem => othersCount += elem.y);

  if (othersCount > 0) {
    dataSeries.push({
      name: i18n.t('influencers.common.others'),
      y: othersCount
    });
  }

  return {
    chart: {
      plotBackgroundColor: null,
      plotBorderWidth: null,
      plotShadow: false,
      type: 'pie'
    },
    title: {
      text: title
    },
    tooltip: {
      pointFormat: '{series.name}: <b>{point.percentage:.1f}%</b>'
    },
    accessibility: {
      point: {
        valueSuffix: '%'
      }
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: 'pointer',
        dataLabels: {
          enabled: true,
          format: '<b>{point.name}</b>: {point.percentage:.1f} %'
        }
      }
    },
    series: [{
      name: i18n.t('influencers.common.followers'),
      colorByPoint: true,
      innerSize: '60%',
      size: '40%',
      data: dataSeries
    }],
    lang: {
      noData: i18n.t('influencers.pages.analytics.not_enough_data')
    },
    noData: {
      style: {
        fontWeight: 'bold',
        fontSize: '15px',
        color: '#303030'
      }
    },
    credits: {
      enabled: false
    },
  };
};

const Audience = ({
  data,
  averageFollowers = false,
  authenticFollowers = false,
  monthlyActiveFollowers = false,
  audienceByState = false,
  audienceByCountry = false,
  audienceByCity = false,
  audienceByGender = false,
  audienceByAge = false,
}) => {
  const { t } = useTranslation();
  if (!data) return;

  const optionsByState = createPieChart(
    get(data, 'stats.audience_by_state', null),
    t('influencers.pages.analytics.states')
  );
  const optionsByCountry = createPieChart(
    get(data, 'stats.audience_by_country', null),
    t('influencers.pages.analytics.countries')
  );
  const optionsByGender = createPieChart(
    get(data, 'stats.audience_by_gender', null),
    t('influencers.pages.analytics.gender')
  );
  const optionsByAge = createPieChart(
    get(data, 'stats.audience_by_age', null),
    t('influencers.pages.analytics.age')
  );
  const optionsByCity = get(data, 'stats.audience_by_city', {});

  const showAudienceByState = audienceByState && get(optionsByState, 'series[0].data', []).length;
  const showAudienceByCountry = audienceByCountry && get(optionsByCountry, 'series[0].data', []).length;
  const showAudienceByGender = audienceByGender && get(optionsByGender, 'series[0].data', []).length;
  const showAudienceByAge = audienceByAge && get(optionsByAge, 'series[0].data', []).length;
  const showAudienceByCity = audienceByCity && Object.keys(optionsByCity).length;

  const hasSomethingToShow = averageFollowers
    || authenticFollowers
    || monthlyActiveFollowers
    || showAudienceByState
    || showAudienceByCountry
    || showAudienceByGender
    || showAudienceByAge
    || showAudienceByCity;

  if (!hasSomethingToShow) return null;

  return (<React.Fragment>
    <SimplifiedAudience
      data={data}
      averageFollowers
      authenticFollowers
      monthlyActiveFollowers
    />
    <Row>
      {showAudienceByState ?
        <Col xs={12} md={6}>
          <HighchartsReact
            highcharts={Highcharts}
            options={optionsByState}
            containerProps={{ className: 'border border-dark border-radius-1' }}
          />
        </Col> : null
      }
      {showAudienceByCountry ?
        <Col xs={12} md={6}>
          <HighchartsReact
            highcharts={Highcharts}
            options={optionsByCountry}
            containerProps={{ className: 'border border-dark border-radius-1' }}
          />
        </Col> : null
      }
      {showAudienceByGender ?
        <Col xs={12} md={6}>
          <HighchartsReact
            highcharts={Highcharts}
            options={optionsByGender}
            containerProps={{ className: 'border border-dark border-radius-1' }}
          />
        </Col> : null
      }
      {showAudienceByAge ?
        <Col xs={12} md={6}>
          <HighchartsReact
            highcharts={Highcharts}
            options={optionsByAge}
            containerProps={{ className: 'border border-dark border-radius-1' }}
          />
        </Col> : null
      }
    </Row>

    {
      showAudienceByCity ?
        <React.Fragment>
          <h4 className='mb-5'>
            {i18n.t('influencers.pages.analytics.cities')}
          </h4>

          <div className="mb-6">
            <TendenceTable data={optionsByCity} />
          </div>
        </React.Fragment> : null
    }

  </React.Fragment >);
};

Audience.propTypes = {
  data: PropTypes.any.isRequired,
  averageFollowers: PropTypes.bool,
  authenticFollowers: PropTypes.bool,
  monthlyActiveFollowers: PropTypes.bool,
  audienceByState: PropTypes.bool,
  audienceByCountry: PropTypes.bool,
  audienceByCity: PropTypes.bool,
  audienceByGender: PropTypes.bool,
  audienceByAge: PropTypes.bool,
};

Audience.defaultProps = {
  averageFollowers: false,
  authenticFollowers: false,
  monthlyActiveFollowers: false,
  audienceByState: false,
  audienceByCountry: false,
  audienceByCity: false,
  audienceByGender: false,
  audienceByAge: false,
};

export default React.memo(Audience);
